// Batch 5 — equipment on events (pure, tested in equipmentLogic.test.ts).
// Strings: equipmentStrings.ts.
import { EQ } from './equipmentStrings'
import type { EquipmentClash, PlanReturnable } from './planTypes'

const EPS = 1e-6

function qty(n: number): string {
  return String(Math.round(n * 1000) / 1000)
}

/** "Sat 31 Oct" for an ISO date, read as a calendar day (no time zone). */
export function shortDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

/** One warning line: "Chafing dishes: 12 asked here, 10 already booked for Sat 31 Oct — you own 18". */
export function clashLine(c: EquipmentClash): string {
  const unit = c.unit && c.unit !== 'each' ? ` ${c.unit}` : ''
  return EQ.clashLine(c.name, qty(c.asked_here) + unit, c.booked_elsewhere > EPS ? qty(c.booked_elsewhere) + unit : null, shortDay(c.event_date), qty(c.owned) + unit)
}

/** "Booked on CAT-1 (4), PLN-002 (6)", or '' when nothing else asks. */
export function clashRefs(c: EquipmentClash): string {
  if (!c.elsewhere?.length) return ''
  return EQ.bookedOn(c.elsewhere.map((e) => `${e.ref} (${qty(e.qty)})`).join(', '))
}

/** The returnable lines of a booking draft, for the form's check; null when
 *  there are none (no request needed). */
export function returnableAsk(extras: { stock_item_id: string; qty: number; is_returnable: boolean }[]): { stock_item_id: string; qty: number }[] | null {
  const out = extras.filter((x) => x.is_returnable && x.qty > 0).map((x) => ({ stock_item_id: x.stock_item_id, qty: x.qty }))
  return out.length ? out : null
}

// ── Send equipment back, with lost or broken ────────────────────────────────

export type WriteOffKind = 'broken' | 'lost'

export interface EquipmentDraft {
  back: number
  lost: number
  kind: WriteOffKind
  note: string
}

/** What can still be sent back or written off (an older server: what is out). */
export function toSettle(r: PlanReturnable): number {
  return Math.max(r.to_settle ?? r.outstanding, 0)
}

/** Something is still out and not already on its way back. */
export function canSettleEquipment(rows: PlanReturnable[] | undefined): boolean {
  return (rows ?? []).some((r) => toSettle(r) > EPS)
}

/** Everything still out comes back, nothing written off — the chef corrects. */
export function initialEquipmentDrafts(rows: PlanReturnable[]): Record<string, EquipmentDraft> {
  const out: Record<string, EquipmentDraft> = {}
  for (const r of rows) out[r.stock_item_id] = { back: toSettle(r), lost: 0, kind: 'broken', note: '' }
  return out
}

export type EquipmentProblem = 'nothing' | 'tooMuch' | 'pin'

export function equipmentProblem(rows: PlanReturnable[], drafts: Record<string, EquipmentDraft>, pin: string): EquipmentProblem | null {
  let any = false
  let lost = false
  for (const r of rows) {
    const d = drafts[r.stock_item_id]
    if (!d) continue
    if (d.back < 0 || d.lost < 0 || d.back + d.lost > toSettle(r) + EPS) return 'tooMuch'
    if (d.back > EPS || d.lost > EPS) any = true
    if (d.lost > EPS) lost = true
  }
  if (!any) return 'nothing'
  if (lost && !pin.trim()) return 'pin'
  return null
}

export function needsWriteOffPin(drafts: Record<string, EquipmentDraft>): boolean {
  return Object.values(drafts).some((d) => d.lost > EPS)
}

/** The POST …/return-equipment body. `lines` is always sent (an empty list
 *  means "nothing back this time", never "send everything"). */
export function equipmentReturnBody(drafts: Record<string, EquipmentDraft>, pin: string) {
  const lines: { stock_item_id: string; qty: number }[] = []
  const writeOffs: { stock_item_id: string; qty: number; kind: WriteOffKind; note?: string }[] = []
  for (const [id, d] of Object.entries(drafts)) {
    if (d.back > EPS) lines.push({ stock_item_id: id, qty: d.back })
    if (d.lost > EPS) writeOffs.push({ stock_item_id: id, qty: d.lost, kind: d.kind, ...(d.note.trim() ? { note: d.note.trim() } : {}) })
  }
  return writeOffs.length ? { lines, write_offs: writeOffs, pin: pin.trim() } : { lines }
}

/** The button's words for what will happen. */
export function equipmentSubmitLabel(drafts: Record<string, EquipmentDraft>): string {
  const back = Object.values(drafts).some((d) => d.back > EPS)
  const lost = needsWriteOffPin(drafts)
  return back && lost ? EQ.submitBoth : lost ? EQ.submitWriteOff : EQ.submitBack
}
